import { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Stack,
  TextField,
  CircularProgress,
  Autocomplete,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Navigate } from "react-router-dom";
import RatingBar from "@/components/RatingBar";
import StepNav from "@/components/StepNav";
import { UniversityEntry, useFetchScoresBatch } from "apiRequests/useScores";
import { useUniversitiesSearch, University } from "apiRequests/useUniversities";
import { useSaveFavorites } from "apiRequests/useFavorites";
import { useAnswers } from "apiRequests/useAnswers";

const BATCH_SIZE = 3;

const isFilled = (section: any) =>
  !!section && typeof section === "object" && Object.keys(section).length > 0;

// Scores need the profile (personalInformation) and academics sections, the same
// check the API makes before scoring. Returns the first unfinished survey step,
// in sidebar order, or null when the survey is filled out.
const unfinishedSurveyStep = (answers: any): string | null => {
  if (isFilled(answers?.personalInformation) && isFilled(answers?.academics)) return null;
  if (!isFilled(answers?.personalInformation)) return "/profile";
  if (!isFilled(answers?.initialQuestions)) return "/initial-questionnaire";
  return "/academics";
};

export default function ResultsCard() {
  const { data: answersData, refetch: refetchAnswers, isReady: answersLoaded, error: answersError } = useAnswers();
  const { search } = useUniversitiesSearch();
  const { save: saveFavorites } = useSaveFavorites();
  const { fetchBatch } = useFetchScoresBatch();

  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState<University[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [displayedScores, setDisplayedScores] = useState<UniversityEntry[]>([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const fetchInProgressRef = useRef(false);

  // Get current favorites from answers
  const getCurrentFavorites = (): University[] => {
    if (answersData?.favorites && Array.isArray(answersData.favorites) && answersData.favorites.length > 0) {
      return answersData.favorites;
    }
    return [];
  };

  // Show universities from favorites immediately with placeholder scores, then fetch in batches
  useEffect(() => {
    // Sort alphabetically so placeholders render and scores are requested
    // (and therefore stream in) in alphabetical order.
    const favorites = [...getCurrentFavorites()].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "")
    );
    if (favorites.length === 0 || fetchInProgressRef.current) return;

    // Create placeholders for all favorites
    const placeholders: UniversityEntry[] = favorites.map(fav => ({
      id: fav.id,
      name: fav.name,
      state: fav.state,
      score: -1, // -1 indicates "calculating"
      formula: "",
      testData: { id: "", sat_math_25: 0, sat_math_75: 0, sat_read_25: 0, sat_read_75: 0 },
      admitRateData: { id: "", Percent_of_applicants_admitted: "" },
      university: fav as any,
    }));
    setDisplayedScores(placeholders);

    // Fetch scores in batches, display one by one with delay based on fetch time
    const fetchScoresInBatches = async () => {
      if (fetchInProgressRef.current) return;
      fetchInProgressRef.current = true;
      setIsCalculating(true);

      const favoriteIds = favorites.map(f => f.id);

      // Split into batches
      for (let i = 0; i < favoriteIds.length; i += BATCH_SIZE) {
        const batchIds = favoriteIds.slice(i, i + BATCH_SIZE);

        try {
          const startTime = Date.now();
          const batchResults = await fetchBatch(batchIds);
          const fetchTime = Date.now() - startTime;

          // Reveal in the requested (alphabetical) order, not the order the
          // API happened to return them, so the staggered delay runs 1, 2, 3
          // top to bottom.
          const orderedResults = batchIds
            .map(id => batchResults.find(r => r.id === id))
            .filter((r): r is UniversityEntry => Boolean(r));

          // Delay between each score = fetch time / number of results
          const delayPerScore = orderedResults.length > 1 ? fetchTime / orderedResults.length : 0;

          // Display each result one by one with calculated delay
          for (let j = 0; j < orderedResults.length; j++) {
            const result = orderedResults[j];
            setDisplayedScores(prev =>
              prev.map(score => score.id === result.id ? result : score)
            );
            // Delay before showing next (skip delay for last item)
            if (j < orderedResults.length - 1) {
              await new Promise(resolve => setTimeout(resolve, delayPerScore));
            }
          }
        } catch (err) {
          console.error("Error fetching batch:", err);
        }
      }

      setIsCalculating(false);
      fetchInProgressRef.current = false;
    };

    fetchScoresInBatches();
  }, [answersData]);

  // Search universities when input changes
  useEffect(() => {
    if (searchValue.length < 2) {
      setOptions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await search(searchValue);
        // Filter out universities already in favorites
        const currentFavorites = getCurrentFavorites();
        const favoriteIds = currentFavorites.map(f => f.id);
        const filtered = results.filter(u => !favoriteIds.includes(u.id));
        setOptions(filtered);
      } catch (err) {
        console.error("Search error:", err);
        setOptions([]);
      }
      setSearchLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  const handleAddSchool = async (university: University | null) => {
    if (!university) return;

    // Optimistic update - add to display immediately with placeholder score
    const placeholderEntry: UniversityEntry = {
      id: university.id,
      name: university.name,
      state: university.state,
      score: -1, // -1 indicates "calculating"
      formula: "",
      testData: { id: "", sat_math_25: 0, sat_math_75: 0, sat_read_25: 0, sat_read_75: 0 },
      admitRateData: { id: "", Percent_of_applicants_admitted: "" },
      university: university as any,
    };
    setDisplayedScores(prev => [...prev, placeholderEntry]);

    const currentFavorites = getCurrentFavorites();
    const newFavorites = [...currentFavorites, university];

    try {
      await saveFavorites(newFavorites);
      setSearchValue("");
      setOptions([]);
      refetchAnswers();

      // Fetch score for just this university
      const results = await fetchBatch([university.id]);
      if (results.length > 0) {
        setDisplayedScores(prev =>
          prev.map(s => s.id === university.id ? results[0] : s)
        );
      }
    } catch (err) {
      console.error("Error saving favorite:", err);
      // Revert on error
      setDisplayedScores(prev => prev.filter(s => s.id !== university.id));
    }
  };

  const handleRemoveSchool = async (universityId: string) => {
    // Optimistic update - remove from display immediately
    const previousScores = displayedScores;
    setDisplayedScores(prev => prev.filter(s => s.id !== universityId));

    const currentFavorites = getCurrentFavorites();
    const newFavorites = currentFavorites.filter(f => f.id !== universityId);

    try {
      await saveFavorites(newFavorites);
      refetchAnswers(); // Update answers but don't recalculate scores
    } catch (err) {
      console.error("Error removing favorite:", err);
      // Revert on error
      setDisplayedScores(previousScores);
    }
  };

  // Survey not filled out: send the student back to finish it. Only once the
  // answers have actually loaded, so a slow or failed request never redirects.
  const surveyStep = answersLoaded ? unfinishedSurveyStep(answersData) : null;
  if (surveyStep) {
    return <Navigate to={surveyStep} replace />;
  }

  return (
    <Card elevation={2} sx={{ borderRadius: 2, backgroundColor: "white" }}>
      <CardContent>
        <Box display="flex" justifyContent="center" mb={3}>
          <Autocomplete
            freeSolo
            options={options}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.name
            }
            loading={searchLoading}
            inputValue={searchValue}
            onInputChange={(_, value) => setSearchValue(value)}
            onChange={(_, value) => handleAddSchool(value as University)}
            sx={{ width: '100%', maxWidth: 480 }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Add a school"
                variant="outlined"
                sx={{ width: '100%' }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name} - {option.city}, {option.state}
              </li>
            )}
          />
        </Box>

        <Typography variant="h6" fontWeight={600} mb={3}>
          Results
        </Typography>

        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          Matches
        </Typography>

        {displayedScores.length === 0 && answersError && !answersLoaded ? (
          <Box textAlign="center" my={10}>
            <Typography variant="body1" color="text.secondary">
              We couldn't load your schools. Please refresh the page.
            </Typography>
          </Box>
        ) : displayedScores.length === 0 && answersLoaded && getCurrentFavorites().length === 0 ? (
          <Box textAlign="center" my={10}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Please add favorites
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Search for a school above to add it and see your match.
            </Typography>
          </Box>
        ) : displayedScores.length === 0 ? (<p style={{ display: 'flex', justifyContent: 'center', margin: '10em auto 10em auto' }}><CircularProgress /></p>) : (
          <Stack spacing={3}>
            {[...displayedScores]
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((row: UniversityEntry, index=0) => (
              <Box key={row.id || index}>
                <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                  <Avatar src={row.university?.homePage || ""} sx={{ width: 32, height: 32 }} />
                  <Typography variant="body1" fontWeight={500} sx={{ flex: 1 }}>
                    {row.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSchool(row.id)}
                    sx={{ color: 'grey.500', '&:hover': { color: 'error.main' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
                {row.score === -1 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Calculating scores...
                  </Typography>
                ) : (
                  <RatingBar value={row.score} />
                )}
              </Box>
            ))}
          </Stack>
        )}
        <StepNav />
      </CardContent>
    </Card>
  );
}
